import { useContext } from "react";
import {
  actionModeSet,
  deleteItemSet,
  IClass,
  selectClassesActionMode,
  selectDeleteItem,
  updateItemSet,
} from "features/Classes";
import { actionModeSet as studentActionModeSet } from "features/Students";
import TableBody from "@mui/material/TableBody";
import EditIcon from "components/SVGs/Edit";
import DeleteIcon from "components/SVGs/Delete";
import TableRow from "@mui/material/TableRow";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { styled } from "@mui/material/styles";
import { SvgIconProps } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { ActionType } from "features/common/Interfaces";
import { useDeleteClassMutation } from "app/services/classes";
import useToast from "hooks/useToast";
import ConfirmMessage from "components/ConfirmMessage";
import { AddIcon } from "components/SVGs";
import { FireBaseContext } from "contexts/FireBase";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "rgb(255 255 255)",
    color: "rgb(62 77 91)",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: "16px 20px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "& td, & th": {
    color: "rgba(105 123 140)",
  },
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

type Props = {
  list: IClass[];
};

const TBody = ({ list }: Props) => {
  const { logEvent } = useContext(FireBaseContext);
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const [deleteClass] = useDeleteClassMutation();
  const actionMode = useAppSelector((state) => selectClassesActionMode(state));
  const deleteItem = useAppSelector((state) => selectDeleteItem(state));
  const editClassHandler = (item: IClass) => {
    dispatch(updateItemSet(item));
    dispatch(actionModeSet(ActionType.update));
    logEvent("ClickOnEditClass");
  };
  const DeleteConfirmHandler = (item: IClass) => {
    dispatch(actionModeSet(ActionType.delete));
    dispatch(deleteItemSet(item));
    logEvent("ClickOnDeleteTeacher");
  };
  const addStudentHandler = (item: IClass) => {
    dispatch(studentActionModeSet(ActionType.add));
    navigate(`/accounts/classes/${item.id}`);
  };
  const deleteClassHandler = async (answer: "yes" | "no") => {
    if (answer === "yes") {
      try {
        if (deleteItem) {
          const data = await deleteClass(deleteItem.id).unwrap();
          if (data?._statusCode === 200) {
            toast({
              label: t("success"),
              message: t("Class Deleted"),
              rtl: i18n.dir() === "rtl",
              type: "success",
              toastId: "classUpdated",
            });
          } else if (data?._statusCode === 400) {
            toast({
              label: t("error"),
              message: t("Cant' delete class"),
              rtl: i18n.dir() === "rtl",
              type: "error",
              toastId: "classUpdated",
            });
          } else throw data;
        }
      } catch (error) {
        console.log({ error });
      }
    }
    dispatch(deleteItemSet(null));
    dispatch(actionModeSet(ActionType.list));
    logEvent("ClickOnConfirmDeleteClass");
  };

  const actionList = (item: IClass) => {
    return {
      ...item,
      addStudent: (
        <AddIcon
          onClick={() => addStudentHandler(item)}
          sx={{
            cursor: "pointer",
            "& path": {
              fill: "rgb(105, 123, 140)",
            },
            "&:hover path": {
              fill: "rgb(97, 194, 238)",
            },
            "&:active path": {
              fill: "rgb(105, 123, 140)",
            },
          }}
        />
      ),
      edit: (
        <EditIcon
          onClick={() => editClassHandler(item)}
          sx={{
            cursor: "pointer",
            "& path": {
              fill: "rgb(105, 123, 140)",
            },
            "&:hover path": {
              fill: "rgb(97, 194, 238)",
            },
            "&:active path": {
              fill: "rgb(105, 123, 140)",
            },
          }}
        />
      ),
      delete: (
        <DeleteIcon
          onClick={() => DeleteConfirmHandler(item)}
          sx={{
            cursor: "pointer",
            "& path": {
              fill: "rgb(105, 123, 140)",
            },
            "&:hover path": {
              fill: "rgb(97, 194, 238)",
            },
            "&:active path": {
              fill: "rgb(105, 123, 140)",
            },
          }}
        />
      ),
    };
  };

  const enhancedList = list.map((item) => actionList(item));

  const align = i18n.dir() === "rtl" ? "right" : "left";
  const deleteDescription = (
    <>
      <div>{t("Do you want to delete class")}</div>
      <div style={{ fontWeight: "bold" }}>{deleteItem?.name}</div>
    </>
  );
  return (
    <>
      <ConfirmMessage
        open={ActionType.delete === actionMode}
        onClose={deleteClassHandler}
        description={deleteDescription}
        title={t("Delete Class")}
      />
      <TableBody>
        {enhancedList.map(
          (
            row: IClass & {
              addStudent: React.ReactElement<SvgIconProps>;
              edit: React.ReactElement<SvgIconProps>;
              delete: React.ReactElement<SvgIconProps>;
            }
          ) => (
            <StyledTableRow key={row.id}>
              <StyledTableCell
                component="th"
                scope="row"
                align={align}
                onClick={() => navigate(`/accounts/classes/${row.id}`)}
                sx={{
                  textDecoration: "underline",
                  cursor: "pointer",
                  color: "rgb(98, 195, 238) !important",
                  fontWeight: "bold",
                }}
              >
                {row.name}
              </StyledTableCell>
              <StyledTableCell align={align} sx={{ width: "20%" }}>
                {t(row.grade)}
              </StyledTableCell>
              <StyledTableCell align={align} sx={{ width: "20%" }}>
                {row.teacher}
              </StyledTableCell>
              <StyledTableCell align={align} sx={{ width: "14%" }}>
                {row.students}
              </StyledTableCell>
              <StyledTableCell align="center" sx={{ width: "70px" }}>
                {row.addStudent}
              </StyledTableCell>
              <StyledTableCell align="center">{row.edit}</StyledTableCell>
              <StyledTableCell align="center">{row.delete}</StyledTableCell>
            </StyledTableRow>
          )
        )}
      </TableBody>
    </>
  );
};

export default TBody;
